/* eslint-disable */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { Check, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { isAdminUser } from '../userUtils';

const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8', yellowDark:'#E4AC49',
  orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF',
  cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3',
  skyBlue:'#BCD9E5', skyBlueLight:'#EAF4F8', skyText:'#3A8FA3',
  white:'#FFFFFF', red:'#DC3545', redLight:'#FFF0F0',
  pink:'#F2B8CB', pinkLight:'#FDF0F5', pinkText:'#C4607A',
  amber:'#E07B00', amberLight:'#FFF3E0',
  purple:'#8B7EC8', purpleLight:'#F0EEF9',
  lm:'#5B8A6F', lmLight:'#E8F2ED', // Lekker Mate brand colour
};

const VENUES       = ['Box Jordaan', 'Box Plantage', 'Sassys'];
const MONTHS       = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS        = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
const LOG_CATEGORIES = [
  { value: 'capex',          label: '💰 Capex / Asset Purchase' },
  { value: 'board_decision', label: '🗳️ Board Decision' },
  { value: 'legal',          label: '⚖️ Legal / Compliance' },
  { value: 'personnel',      label: '👥 Personnel Change' },
  { value: 'regulatory',     label: '📋 Regulatory / Permit' },
  { value: 'distribution',   label: '💸 Distribution / Dividend' },
  { value: 'other',          label: '📝 Other' },
];

function getWeekNumber(date) {
  const d = new Date(date);
  d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

function weeksInYear(year) {
  const dec28 = new Date(year, 11, 28);
  return getWeekNumber(dec28);
}

function weekMonday(week, year) {
  const jan4 = new Date(year, 0, 4);
  const startOfW1 = new Date(jan4);
  startOfW1.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
  const monday = new Date(startOfW1);
  monday.setDate(startOfW1.getDate() + (week - 1) * 7);
  return monday;
}

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtMonday(week, year) {
  const d = weekMonday(week, year);
  return `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`;
}

const currentWeek    = getWeekNumber(new Date());
const currentMonth   = new Date().getMonth() + 1;
const currentQuarter = Math.ceil(currentMonth / 3);
const QUARTERS       = [
  { label:'Q1', months:[1,2,3] },
  { label:'Q2', months:[4,5,6] },
  { label:'Q3', months:[7,8,9] },
  { label:'Q4', months:[10,11,12] },
];

const fmt   = (n) => (n != null && n !== 0) ? `€${Number(n).toLocaleString('en-US', { minimumFractionDigits:0, maximumFractionDigits:0 })}` : '\u2014';
const toNum = (v) => parseFloat(v) || 0;
const toInt = (v) => parseInt(v)   || 0;

// ── Styles ────────────────────────────────────────────────────────────────────
const inputStyle = {
  width:'100%', padding:'8px 10px',
  border:`1.5px solid ${C.border}`, borderRadius:'8px',
  fontSize:'13px', boxSizing:'border-box',
  background:C.white, color:C.charcoal, outline:'none', fontFamily:'inherit',
  transition:'border-color 0.15s',
};
const labelStyle = {
  fontSize:'11px', fontWeight:700, color:C.mid,
  display:'block', marginBottom:'5px',
  textTransform:'uppercase', letterSpacing:'0.05em',
};
const card = {
  background:C.white, borderRadius:'14px',
  border:`1.5px solid ${C.border}`, padding:'20px 24px',
  boxShadow:'0 2px 8px rgba(0,0,0,0.05)',
};
const pillStyle = (active, color) => ({
  padding:'6px 16px', borderRadius:'20px',
  border: active ? 'none' : `1.5px solid ${C.border}`,
  cursor:'pointer', fontSize:'13px', fontWeight:600,
  background: active ? (color || C.charcoal) : C.cream,
  color: active ? C.white : C.mid, transition:'all 0.15s',
});
const btnPrimary = (disabled, color) => ({
  padding:'10px 20px',
  background: disabled ? C.border : (color || C.charcoal),
  color:C.white, border:'none', borderRadius:'10px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize:'13px', fontWeight:700, transition:'opacity 0.15s',
  opacity: disabled ? 0.6 : 1,
});
const btnSecondary = {
  padding:'8px 14px', background:C.cream,
  color:C.charcoal, border:`1.5px solid ${C.border}`,
  borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:600,
};
const sectionBand = (color) => ({
  fontSize:'11px', fontWeight:800, color: color || C.navy,
  textTransform:'uppercase', letterSpacing:'0.08em',
  padding:'7px 12px',
  background: color ? `${color}18` : C.cream,
  borderRadius:'7px', marginBottom:'12px', marginTop:'18px',
  borderLeft:`3px solid ${color || C.navy}`,
});

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, type }) {
  if (!message) return null;
  const bg = type === 'success' ? C.green : type === 'warning' ? C.amber : C.red;
  return (
    <div style={{
      position:'fixed', bottom:'24px', right:'24px', zIndex:9999,
      padding:'13px 20px', borderRadius:'12px',
      background:bg, color:C.white, fontSize:'13px', fontWeight:700,
      boxShadow:'0 6px 24px rgba(0,0,0,0.18)',
      display:'flex', alignItems:'center', gap:'9px',
    }}>
      {type === 'success' ? <Check size={15}/> : <AlertCircle size={15}/>}
      {message}
    </div>
  );
}

// ── Stat chip ─────────────────────────────────────────────────────────────────
function StatChip({ label, value, color, sub }) {
  return (
    <div style={{
      background: color ? `${color}12` : C.cream,
      border:`1.5px solid ${color ? `${color}30` : C.border}`,
      borderRadius:'10px', padding:'10px 14px', flex:1,
    }}>
      <div style={{ fontSize:'10px', fontWeight:700, color:color||C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'4px' }}>{label}</div>
      <div style={{ fontSize:'18px', fontWeight:800, color:color||C.charcoal }}>{value}</div>
      {sub && <div style={{ fontSize:'10px', color:C.mid, marginTop:'2px' }}>{sub}</div>}
    </div>
  );
}

// ── Uncontrolled cell ─────────────────────────────────────────────────────────
function NumCell({ initialValue, onCommit, placeholder='', style={} }) {
  const ref     = useRef(null);
  const focused = useRef(false);
  useEffect(() => {
    if (ref.current && !focused.current) {
      ref.current.value = (initialValue != null && initialValue !== 0) ? String(initialValue) : '';
    }
  }, [initialValue]);
  return (
    <input ref={ref} type='number'
      defaultValue={(initialValue != null && initialValue !== 0) ? initialValue : ''}
      placeholder={placeholder}
      onFocus={() => { focused.current = true; }}
      onBlur={e => { focused.current = false; onCommit(e.target.value); }}
      style={{ ...inputStyle, ...style }}
    />
  );
}

// ── Debounced input ───────────────────────────────────────────────────────────
function DebounceInput({ value, onChange, placeholder='', style={}, type='number' }) {
  const [local, setLocal] = useState(value ?? '');
  const timer = useRef(null);
  useEffect(() => { setLocal(value ?? ''); }, [value]);
  const handleChange = (e) => {
    setLocal(e.target.value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(e.target.value), 300);
  };
  return <input type={type} value={local} onChange={handleChange} placeholder={placeholder} style={{ ...inputStyle, ...style }}/>;
}

// ── Row layout helper ─────────────────────────────────────────────────────────
// 3-col: label | actual | budget
function CostRowRaw({ label, actualNode, budgetNode, pctLabel }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 1fr', gap:'8px', alignItems:'center', marginBottom:'8px' }}>
      <div style={{ fontSize:'12px', color:C.charcoal, fontWeight:600 }}>
        {label}
        {pctLabel && <span style={{ fontSize:'10px', color:C.mid, marginLeft:'5px', background:C.cream, padding:'1px 5px', borderRadius:'4px' }}>{pctLabel}</span>}
      </div>
      {actualNode}
      {budgetNode || <div/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — Weekly Matrix (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
function WeeklyTab({ venue }) {
  const [year, setYear]       = useState(CURRENT_YEAR);
  const [records, setRecords] = useState({});
  const [dirty, setDirty]     = useState({});
  const [saving, setSaving]   = useState({});
  const [drafts, setDrafts]   = useState({});
  const [toast, setToast]     = useState(null);

  const isSassys  = venue === 'Sassys';
  const col1Label = isSassys ? 'Lunch €' : 'Brunch €';

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    const { data } = await supabase.from('results').select('*').eq('venue', venue).eq('year', year).order('week');
    const map = {};
    if (data) data.forEach(r => { map[r.week] = r; });
    setRecords(map); setDirty({}); setDrafts({});
  }, [venue, year]);

  useEffect(() => { load(); }, [load]);

  const totalWeeks = weeksInYear(year);
  const weeks = Array.from({ length: totalWeeks }, (_, i) => i + 1);

  const getDraft = (w) => drafts[w] || {
    brunch_revenue: records[w]?.brunch_revenue || '',
    dinner_revenue: records[w]?.dinner_revenue || '',
    total_covers:   records[w]?.total_covers   || '',
    total_hours:    records[w]?.total_hours    || '',
  };

  const setDraftField = (w, field, val) => {
    setDrafts(prev => ({ ...prev, [w]: { ...getDraft(w), [field]: val } }));
    setDirty(prev => ({ ...prev, [w]: true }));
  };

  const handleSaveRow = async (w) => {
    setSaving(prev => ({ ...prev, [w]: true }));
    const d = getDraft(w);
    const payload = {
      venue, year, week:w,
      brunch_revenue: toNum(d.brunch_revenue),
      dinner_revenue: toNum(d.dinner_revenue),
      total_covers:   toInt(d.total_covers),
      total_hours:    toNum(d.total_hours),
    };
    const existing = records[w];
    if (existing) {
      const { data } = await supabase.from('results').update(payload).eq('id', existing.id).select().single();
      if (data) { setRecords(prev => ({ ...prev, [w]: data })); showToast(`W${w} updated`); }
    } else {
      const { data } = await supabase.from('results').insert([payload]).select().single();
      if (data) { setRecords(prev => ({ ...prev, [w]: data })); showToast(`W${w} saved`); }
    }
    setDirty(prev => ({ ...prev, [w]: false }));
    setDrafts(prev => { const n = { ...prev }; delete n[w]; return n; });
    setSaving(prev => ({ ...prev, [w]: false }));
  };

  const handleDeleteRow = async (w) => {
    if (!window.confirm(`Delete W${w}?`)) return;
    const existing = records[w];
    if (existing) {
      await supabase.from('results').delete().eq('id', existing.id);
      setRecords(prev => { const n = { ...prev }; delete n[w]; return n; });
      showToast(`W${w} deleted`);
    }
  };

  const savedCount = Object.keys(records).length;
  const totalRev   = Object.values(records).reduce((s, r) => s + (r.brunch_revenue||0) + (r.dinner_revenue||0), 0);

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type}/>}
      <div style={{ display:'flex', gap:'8px', marginBottom:'20px', alignItems:'center', flexWrap:'wrap' }}>
        {YEARS.map(y => <button key={y} onClick={() => setYear(y)} style={pillStyle(year===y, C.navy)}>{y}</button>)}
        <div style={{ marginLeft:'auto', display:'flex', gap:'10px' }}>
          <StatChip label='Weeks entered' value={`${savedCount}/${totalWeeks}`} color={C.skyText}/>
          {totalRev > 0 && <StatChip label='Total revenue' value={fmt(totalRev)} color={C.green}/>}
        </div>
      </div>
      <div style={{ background:C.white, borderRadius:'14px', border:`1.5px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', maxWidth:'800px' }}>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
            <colgroup>
              <col style={{ width:'140px' }}/>
              <col style={{ width:'16%' }}/>
              <col style={{ width:'16%' }}/>
              <col style={{ width:'13%' }}/>
              <col style={{ width:'13%' }}/>
              <col style={{ width:'13%' }}/>
              <col style={{ width:'60px' }}/>
            </colgroup>
            <thead>
              <tr style={{ background:C.charcoal }}>
                {['Week', col1Label, 'Dinner €', 'Total', 'Covers', 'Hours', ''].map((h,i) => (
                  <th key={i} style={{ padding:'10px 12px', textAlign:i===0?'left':i===6?'center':'right', fontSize:'11px', fontWeight:700, color:i===0?C.yellow:'rgba(255,255,255,0.7)', textTransform:'uppercase', letterSpacing:'0.06em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((w, idx) => {
                const saved     = records[w];
                const isDirty   = dirty[w];
                const isSaving  = saving[w];
                const draft     = getDraft(w);
                const isCurrent = w === currentWeek && year === CURRENT_YEAR;
                const brunch    = isDirty ? toNum(draft.brunch_revenue) : (saved?.brunch_revenue||0);
                const dinner    = isDirty ? toNum(draft.dinner_revenue) : (saved?.dinner_revenue||0);
                const total     = brunch + dinner;
                let rowBg = idx % 2 === 0 ? C.white : '#FAFAFA';
                if (isDirty) rowBg = C.yellowLight;
                if (isCurrent && !isDirty) rowBg = C.skyBlueLight;
                return (
                  <tr key={w} style={{ borderBottom:`1px solid ${C.border}`, background:rowBg }}>
                    <td style={{ padding:'6px 12px', whiteSpace:'nowrap' }}>
                      <span style={{ fontWeight:700, fontSize:'13px', color:isCurrent?C.skyText:C.charcoal }}>W{w}</span>
                      <span style={{ fontSize:'11px', color:C.mid, marginLeft:'6px' }}>{fmtMonday(w, year)}</span>
                      {isCurrent && <span style={{ fontSize:'9px', background:C.skyBlue, color:C.skyText, marginLeft:'5px', fontWeight:700, padding:'2px 5px', borderRadius:'4px' }}>NOW</span>}
                    </td>
                    <td style={{ padding:'4px 6px' }}><NumCell initialValue={saved?.brunch_revenue} onCommit={v => setDraftField(w,'brunch_revenue',v)} placeholder='0' style={{ textAlign:'right', background:saved&&!isDirty?'#FAFAFA':C.white }}/></td>
                    <td style={{ padding:'4px 6px' }}><NumCell initialValue={saved?.dinner_revenue} onCommit={v => setDraftField(w,'dinner_revenue',v)} placeholder='0' style={{ textAlign:'right', background:saved&&!isDirty?'#FAFAFA':C.white }}/></td>
                    <td style={{ padding:'6px 12px', textAlign:'right', fontWeight:800, fontSize:'13px', color:total>0?C.charcoal:C.border, whiteSpace:'nowrap' }}>{total > 0 ? fmt(total) : '\u2014'}</td>
                    <td style={{ padding:'4px 6px' }}><NumCell initialValue={saved?.total_covers} onCommit={v => setDraftField(w,'total_covers',v)} placeholder='0' style={{ textAlign:'right', background:saved&&!isDirty?'#FAFAFA':C.white }}/></td>
                    <td style={{ padding:'4px 6px' }}><NumCell initialValue={saved?.total_hours} onCommit={v => setDraftField(w,'total_hours',v)} placeholder='0' style={{ textAlign:'right', background:saved&&!isDirty?'#FAFAFA':C.white }}/></td>
                    <td style={{ padding:'4px 10px', textAlign:'center', whiteSpace:'nowrap' }}>
                      {isDirty ? (
                        <button onClick={() => handleSaveRow(w)} disabled={isSaving} style={{ padding:'4px 12px', background:C.green, color:C.white, border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'12px', fontWeight:700 }}>
                          {isSaving ? '…' : 'Save'}
                        </button>
                      ) : saved ? (
                        <button onClick={() => handleDeleteRow(w)} style={{ padding:'4px 8px', background:'transparent', color:C.border, border:'none', cursor:'pointer' }} title='Delete'><Trash2 size={13}/></button>
                      ) : <span style={{ fontSize:'11px', color:C.border }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p style={{ fontSize:'11px', color:C.mid, marginTop:'10px' }}>
        💡 Edit any cell and click Save. <span style={{ background:C.yellowLight, padding:'1px 6px', borderRadius:'4px' }}>Yellow</span> = unsaved · <span style={{ background:C.skyBlueLight, padding:'1px 6px', borderRadius:'4px' }}>Blue</span> = current week
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — Monthly P&L
// ─────────────────────────────────────────────────────────────────────────────
const emptyMonth = (isSassys = false) => ({
  month: currentMonth, year: CURRENT_YEAR,
  // Revenue
  food_revenue: '', bev_revenue: '',
  budget_revenue: '',
  actual_covers: '', budget_covers: '',
  // Oedipus (Sassys only)
  oedipus_rent: '', oedipus_utilities: '',
  oedipus_rev_share: '', oedipus_rev_share_pct: '',
  budget_oedipus_rent: '', budget_oedipus_utilities: '', budget_oedipus_rev_share: '',
  // Costs
  food_cost: '', budget_food_cost: '',
  bev_cost: '', budget_bev_cost: '',
  // Payroll — always separate kitchen + FOH
  kitchen_payroll: '', budget_kitchen_payroll: '',
  foh_payroll: '',     budget_foh_payroll: '',
  kitchen_hours: '',   budget_kitchen_hours: '',
  foh_hours: '',       budget_foh_hours: '',
  // Other
  other_opex: '', budget_other_opex: '',
  depreciation: '', budget_depreciation: '',
  net_profit: '', budget_net_profit: '',
});

function MonthlyTab({ venue }) {
  const isSassys = venue === 'Sassys';
  const [records, setRecords]     = useState([]);
  const [form, setForm]           = useState(emptyMonth(isSassys));
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null);
  const [year, setYear]           = useState(CURRENT_YEAR);
  // LM allocations for this venue — read-only, written by LekkerMateTab
  const [lmAllocs, setLmAllocs]   = useState({});

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    const { data } = await supabase.from('results_monthly').select('*').eq('venue', venue).eq('year', year).order('month');
    if (data) setRecords(data);
  }, [venue, year]);

  // Load LM allocations for the month currently in the form
  const loadLmAllocs = useCallback(async (m, y) => {
    const { data } = await supabase.from('results_monthly')
      .select('lm_prep_payroll_alloc, lm_transfers_alloc, group_costs, other_opex')
      .eq('venue', venue).eq('year', y || year).eq('month', m || toInt(form.month))
      .single();
    if (data) setLmAllocs(data);
    else setLmAllocs({});
  }, [venue, year, form.month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLmAllocs(toInt(form.month), year); }, [form.month, year]);
  useEffect(() => { setForm(emptyMonth(isSassys)); setEditingId(null); }, [venue]);

  const set = (key) => (v) => setForm(prev => ({ ...prev, [key]: v }));

  // Revenue
  const foodRev   = toNum(form.food_revenue);
  const bevRev    = isSassys ? 0 : toNum(form.bev_revenue);
  const actualRev = foodRev + bevRev;
  const budgetRev = toNum(form.budget_revenue);

  // Oedipus auto-calc rev share
  const oedipusPct       = toNum(form.oedipus_rev_share_pct);
  const oedipusRevShare  = oedipusPct > 0 && foodRev > 0 ? Math.round(foodRev * oedipusPct / 100) : toNum(form.oedipus_rev_share);
  const budgetRevShare   = oedipusPct > 0 && budgetRev > 0 ? Math.round(budgetRev * oedipusPct / 100) : toNum(form.budget_oedipus_rev_share);

  // EBITDA
  const oedipusCosts = isSassys ? toNum(form.oedipus_rent) + toNum(form.oedipus_utilities) + oedipusRevShare : 0;
  const budgetOedipus = isSassys ? toNum(form.budget_oedipus_rent) + toNum(form.budget_oedipus_utilities) + budgetRevShare : 0;

  const lmPrep      = toNum(lmAllocs.lm_prep_payroll_alloc);
  const lmTransfers = toNum(lmAllocs.lm_transfers_alloc);
  const lmMgmt      = toNum(lmAllocs.group_costs);
  const lmOther     = toNum(lmAllocs.other_opex);
  const lmTotal     = lmPrep + lmTransfers + lmMgmt + lmOther;

  const actualEBITDA = actualRev
    - toNum(form.food_cost)
    - (isSassys ? 0 : toNum(form.bev_cost))
    - oedipusCosts
    - toNum(form.kitchen_payroll)
    - (isSassys ? 0 : toNum(form.foh_payroll))
    - lmTotal;

  const budgetEBITDA = budgetRev
    - toNum(form.budget_food_cost)
    - (isSassys ? 0 : toNum(form.budget_bev_cost))
    - budgetOedipus
    - toNum(form.budget_kitchen_payroll)
    - (isSassys ? 0 : toNum(form.budget_foh_payroll))
    - toNum(form.budget_other_opex);

  const actualEBIT = actualEBITDA - toNum(form.depreciation);
  const budgetEBIT = budgetEBITDA - toNum(form.budget_depreciation);

  const handleSave = async () => {
    setSaving(true);
    const recMonth = toInt(form.month);
    const recYear  = toInt(form.year);
    const computedRevShare = oedipusPct > 0 ? Math.round(foodRev * oedipusPct / 100) : toNum(form.oedipus_rev_share);
    const computedBudgetRevShare = oedipusPct > 0 ? Math.round(budgetRev * oedipusPct / 100) : toNum(form.budget_oedipus_rev_share);

    const payload = {
      venue, year:recYear, month:recMonth,
      food_revenue: foodRev, bev_revenue: bevRev,
      actual_revenue: actualRev, budget_revenue: toNum(form.budget_revenue),
      actual_covers: toInt(form.actual_covers), budget_covers: toInt(form.budget_covers),
      food_cost: toNum(form.food_cost), budget_food_cost: toNum(form.budget_food_cost),
      bev_cost: isSassys ? 0 : toNum(form.bev_cost),
      budget_bev_cost: isSassys ? 0 : toNum(form.budget_bev_cost),
      kitchen_payroll: toNum(form.kitchen_payroll), budget_kitchen_payroll: toNum(form.budget_kitchen_payroll),
      foh_payroll: isSassys ? 0 : toNum(form.foh_payroll),
      budget_foh_payroll: isSassys ? 0 : toNum(form.budget_foh_payroll),
      kitchen_hours: toNum(form.kitchen_hours), budget_kitchen_hours: toNum(form.budget_kitchen_hours),
      foh_hours: isSassys ? 0 : toNum(form.foh_hours),
      budget_foh_hours: isSassys ? 0 : toNum(form.budget_foh_hours),
      // Oedipus (Sassys only — zero for others)
      oedipus_rent: isSassys ? toNum(form.oedipus_rent) : 0,
      oedipus_utilities: isSassys ? toNum(form.oedipus_utilities) : 0,
      oedipus_rev_share: isSassys ? computedRevShare : 0,
      oedipus_rev_share_pct: isSassys ? oedipusPct : 0,
      budget_oedipus_rent: isSassys ? toNum(form.budget_oedipus_rent) : 0,
      budget_oedipus_utilities: isSassys ? toNum(form.budget_oedipus_utilities) : 0,
      budget_oedipus_rev_share: isSassys ? computedBudgetRevShare : 0,
      // other_opex — written by LM tab, don't overwrite on venue save
      depreciation: toNum(form.depreciation), budget_depreciation: toNum(form.budget_depreciation),
      net_profit: toNum(form.net_profit), budget_net_profit: toNum(form.budget_net_profit),
    };

    if (editingId) {
      const { error } = await supabase.from('results_monthly').update(payload).eq('id', editingId);
      if (!error) { showToast(`${MONTHS[recMonth-1]} ${recYear} updated`); load(); setEditingId(null); setForm(emptyMonth(isSassys)); }
      else showToast('Error: ' + error.message, 'error');
    } else {
      const { data: existing } = await supabase.from('results_monthly').select('id')
        .eq('venue', venue).eq('year', recYear).eq('month', recMonth).single();
      if (existing) {
        const { error } = await supabase.from('results_monthly').update(payload).eq('id', existing.id);
        if (!error) { showToast(`${MONTHS[recMonth-1]} ${recYear} updated`); load(); setForm(emptyMonth(isSassys)); }
        else showToast('Error: ' + error.message, 'error');
      } else {
        const { error } = await supabase.from('results_monthly').insert([payload]);
        if (!error) { showToast(`${MONTHS[recMonth-1]} ${recYear} saved`); load(); setForm(emptyMonth(isSassys)); }
        else showToast('Error: ' + error.message, 'error');
      }
    }
    setSaving(false);
  };

  const handleEdit = (r) => {
    setForm({
      month:r.month, year:r.year,
      food_revenue:r.food_revenue||'', bev_revenue:r.bev_revenue||'',
      budget_revenue:r.budget_revenue||'',
      actual_covers:r.actual_covers||'', budget_covers:r.budget_covers||'',
      oedipus_rent:r.oedipus_rent||'', oedipus_utilities:r.oedipus_utilities||'',
      oedipus_rev_share:r.oedipus_rev_share||'', oedipus_rev_share_pct:r.oedipus_rev_share_pct||'',
      budget_oedipus_rent:r.budget_oedipus_rent||'',
      budget_oedipus_utilities:r.budget_oedipus_utilities||'',
      budget_oedipus_rev_share:r.budget_oedipus_rev_share||'',
      food_cost:r.food_cost||'', budget_food_cost:r.budget_food_cost||'',
      bev_cost:r.bev_cost||'', budget_bev_cost:r.budget_bev_cost||'',
      kitchen_payroll:r.kitchen_payroll||'', budget_kitchen_payroll:r.budget_kitchen_payroll||'',
      foh_payroll:r.foh_payroll||'', budget_foh_payroll:r.budget_foh_payroll||'',
      kitchen_hours:r.kitchen_hours||'', budget_kitchen_hours:r.budget_kitchen_hours||'',
      foh_hours:r.foh_hours||'', budget_foh_hours:r.budget_foh_hours||'',
      other_opex:r.other_opex||'', budget_other_opex:r.budget_other_opex||'',
      depreciation:r.depreciation||'', budget_depreciation:r.budget_depreciation||'',
      net_profit:r.net_profit||'', budget_net_profit:r.budget_net_profit||'',
    });
    setEditingId(r.id);
    loadLmAllocs(r.month, r.year);
    window.scrollTo({ top:0, behavior:'smooth' });
  };

  // Inline debounce row: label | actual input | budget input
  const CostRow = ({ label, actualKey, budgetKey, isCovers=false, readOnly=false, autoVal=null, pctDisplay=null }) => {
    const val = autoVal !== null ? autoVal : toNum(form[actualKey]);
    const pctLabel = !isCovers && val > 0 && actualRev > 0 && pctDisplay !== false
      ? ((val / actualRev) * 100).toFixed(1) + '%' : null;
    return (
      <CostRowRaw
        label={label}
        pctLabel={pctLabel}
        actualNode={
          readOnly
            ? <div style={{ padding:'8px 10px', background:C.cream, border:`1.5px solid ${C.border}`, borderRadius:'8px', textAlign:'right', fontSize:'13px', fontWeight:700, color:C.charcoal }}>
                {autoVal !== null && autoVal > 0 ? fmt(autoVal) : '\u2014'}
              </div>
            : <DebounceInput value={form[actualKey]} onChange={set(actualKey)} placeholder='0'/>
        }
        budgetNode={
          budgetKey
            ? <DebounceInput value={form[budgetKey]} onChange={set(budgetKey)} placeholder='0' style={{ background:C.amberLight, borderColor:`${C.amber}40` }}/>
            : <div style={{ padding:'8px 10px', background:C.cream, border:`1.5px solid ${C.border}`, borderRadius:'8px', textAlign:'right', fontSize:'11px', color:C.mid }}>—</div>
        }
      />
    );
  };

  // Read-only LM allocation row
  const LmRow = ({ label, value }) => (
    value > 0 ? (
      <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 1fr', gap:'8px', alignItems:'center', marginBottom:'8px' }}>
        <div style={{ fontSize:'12px', color:C.mid, fontStyle:'italic' }}>↳ {label}</div>
        <div style={{ padding:'8px 10px', background:C.lmLight, border:`1.5px solid ${C.lm}30`, borderRadius:'8px', textAlign:'right', fontSize:'12px', fontWeight:600, color:C.lm }}>
          {fmt(value)}
        </div>
        <div style={{ padding:'8px 10px', background:C.cream, border:`1.5px solid ${C.border}`, borderRadius:'8px', textAlign:'right', fontSize:'11px', color:C.mid }}>from LM</div>
      </div>
    ) : null
  );

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:'24px', alignItems:'start' }}>
      {toast && <Toast message={toast.msg} type={toast.type}/>}

      {/* Form */}
      <div style={card}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'18px' }}>
          <div style={{ width:'4px', height:'28px', background:editingId ? C.amber : C.green, borderRadius:'4px' }}/>
          <div>
            <h3 style={{ margin:0, fontSize:'15px', fontWeight:800, color:C.charcoal }}>
              {editingId ? 'Edit Month' : 'Add / Update Month'}
            </h3>
            {isSassys && <span style={{ fontSize:'11px', color:C.mid }}>Sassys kitchen · no FOH · no bev</span>}
          </div>
        </div>

        {/* Month picker */}
        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'18px', padding:'10px 14px', background:C.cream, borderRadius:'9px', border:`1.5px solid ${C.border}` }}>
          <div style={{ fontSize:'20px', fontWeight:900, color:C.charcoal }}>{MONTHS[toInt(form.month)-1]} {toInt(form.year)}</div>
          {!editingId && (
            <div style={{ marginLeft:'auto', display:'flex', gap:'6px', flexWrap:'wrap' }}>
              {MONTHS.map((m,i) => (
                <button key={i} onClick={() => setForm(f => ({...f, month:i+1}))}
                  style={{ ...(toInt(form.month)===i+1 ? { background:C.navy, color:C.white, border:'none' } : { background:C.white, color:C.mid, border:`1px solid ${C.border}` }), padding:'3px 8px', borderRadius:'5px', cursor:'pointer', fontSize:'11px', fontWeight:600 }}>
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Column headers */}
        <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 1fr', gap:'8px', marginBottom:'8px' }}>
          <div/>
          <div style={{ fontSize:'11px', fontWeight:700, color:C.charcoal, textAlign:'center', background:C.cream, padding:'5px', borderRadius:'6px' }}>Actual</div>
          <div style={{ fontSize:'11px', fontWeight:700, color:C.amber, textAlign:'center', background:C.amberLight, padding:'5px', borderRadius:'6px' }}>Budget</div>
        </div>

        {/* ── Revenue ── */}
        <div style={sectionBand(C.skyText)}>Revenue & Covers</div>
        <CostRow label='Food Revenue (€)' actualKey='food_revenue' budgetKey='budget_revenue'/>
        {!isSassys && <CostRow label='Bev Revenue (€)' actualKey='bev_revenue' budgetKey={null}/>}
        {/* Total Revenue */}
        <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 1fr', gap:'8px', alignItems:'center', marginBottom:'8px' }}>
          <div style={{ fontSize:'12px', color:C.charcoal, fontWeight:700 }}>Total Revenue</div>
          <div style={{ padding:'8px 10px', background:actualRev>0?C.greenLight:C.cream, border:`1.5px solid ${actualRev>0?C.green+'40':C.border}`, borderRadius:'8px', textAlign:'right', fontSize:'13px', fontWeight:800, color:actualRev>0?C.green:C.mid }}>
            {actualRev > 0 ? fmt(actualRev) : '\u2014'}
          </div>
          <div style={{ padding:'8px 10px', background:C.amberLight, border:`1.5px solid ${C.amber}40`, borderRadius:'8px', textAlign:'right', fontSize:'13px', fontWeight:700, color:C.amber }}>
            {budgetRev > 0 ? fmt(budgetRev) : '\u2014'}
          </div>
        </div>
        <CostRow label='Covers' actualKey='actual_covers' budgetKey='budget_covers' isCovers pctDisplay={false}/>

        {/* ── Oedipus (Sassys only) ── */}
        {isSassys && (
          <>
            <div style={sectionBand(C.orange)}>Oedipus Costs</div>
            <CostRow label='Rent (€)' actualKey='oedipus_rent' budgetKey='budget_oedipus_rent'/>
            <CostRow label='Utilities (€)' actualKey='oedipus_utilities' budgetKey='budget_oedipus_utilities'/>
            {/* Rev share % + auto-calc */}
            <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 1fr', gap:'8px', alignItems:'center', marginBottom:'8px' }}>
              <div style={{ fontSize:'12px', color:C.charcoal, fontWeight:600 }}>
                Rev Share
                {oedipusPct > 0 && <span style={{ fontSize:'10px', color:C.mid, marginLeft:'5px', background:C.cream, padding:'1px 5px', borderRadius:'4px' }}>{oedipusPct}%</span>}
              </div>
              <div style={{ display:'flex', gap:'6px' }}>
                <input type='number' value={form.oedipus_rev_share_pct} onChange={e => set('oedipus_rev_share_pct')(e.target.value)}
                  placeholder='%' style={{ ...inputStyle, width:'64px', textAlign:'center' }}/>
                <div style={{ flex:1, padding:'8px 10px', background:oedipusRevShare>0?C.orangeLight:C.cream, border:`1.5px solid ${C.border}`, borderRadius:'8px', textAlign:'right', fontSize:'13px', fontWeight:700, color:C.orange }}>
                  {oedipusRevShare > 0 ? fmt(oedipusRevShare) : '\u2014'}
                </div>
              </div>
              <div style={{ padding:'8px 10px', background:C.amberLight, border:`1.5px solid ${C.amber}40`, borderRadius:'8px', textAlign:'right', fontSize:'13px', fontWeight:700, color:C.amber }}>
                {budgetRevShare > 0 ? fmt(budgetRevShare) : '\u2014'}
              </div>
            </div>
            {/* Oedipus total */}
            <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 1fr', gap:'8px', alignItems:'center', marginBottom:'8px' }}>
              <div style={{ fontSize:'12px', color:C.charcoal, fontWeight:700 }}>Oedipus Total</div>
              <div style={{ padding:'8px 10px', background:oedipusCosts>0?C.redLight:C.cream, border:`1.5px solid ${oedipusCosts>0?C.red+'30':C.border}`, borderRadius:'8px', textAlign:'right', fontSize:'13px', fontWeight:800, color:oedipusCosts>0?C.red:C.mid }}>
                {oedipusCosts > 0 ? fmt(oedipusCosts) : '\u2014'}
              </div>
              <div style={{ padding:'8px 10px', background:C.amberLight, border:`1.5px solid ${C.amber}40`, borderRadius:'8px', textAlign:'right', fontSize:'13px', fontWeight:700, color:C.amber }}>
                {budgetOedipus > 0 ? fmt(budgetOedipus) : '\u2014'}
              </div>
            </div>
          </>
        )}

        {/* ── Cost of Sales ── */}
        {!isSassys && (
          <>
            <div style={sectionBand(C.red)}>Cost of Sales</div>
            <CostRow label='Food Cost (€)' actualKey='food_cost' budgetKey='budget_food_cost'/>
            <CostRow label='Bev Cost (€)'  actualKey='bev_cost'  budgetKey='budget_bev_cost'/>
          </>
        )}
        {isSassys && (
          <>
            <div style={sectionBand(C.red)}>Cost of Sales</div>
            <CostRow label='Food Cost (€)' actualKey='food_cost' budgetKey='budget_food_cost'/>
          </>
        )}

        {/* ── Payroll ── */}
        <div style={sectionBand(C.purple)}>Payroll</div>
        <CostRow label='Kitchen Payroll (€)' actualKey='kitchen_payroll' budgetKey='budget_kitchen_payroll'/>
        <CostRow label='Kitchen Hours'        actualKey='kitchen_hours'   budgetKey='budget_kitchen_hours' isCovers pctDisplay={false}/>
        {!isSassys && (
          <>
            <CostRow label='FOH Payroll (€)' actualKey='foh_payroll' budgetKey='budget_foh_payroll'/>
            <CostRow label='FOH Hours'        actualKey='foh_hours'   budgetKey='budget_foh_hours' isCovers pctDisplay={false}/>
          </>
        )}

        {/* ── LM Allocations (read-only, written by LM tab) ── */}
        {lmTotal > 0 && (
          <>
            <div style={sectionBand(C.lm)}>Lekker Mate Allocations</div>
            <div style={{ fontSize:'11px', color:C.mid, marginBottom:'10px', padding:'7px 10px', background:C.lmLight, borderRadius:'7px', border:`1px solid ${C.lm}30` }}>
              ℹ️ Set and saved via the Lekker Mate tab. Edit there to change allocations.
            </div>
            <LmRow label='Prep Kitchen Payroll' value={lmPrep}/>
            <LmRow label='Food Transfers'        value={lmTransfers}/>
            <LmRow label='Management Fee'        value={lmMgmt}/>
            <LmRow label='Other Shared Costs'    value={lmOther}/>
            {/* LM Total */}
            <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 1fr', gap:'8px', alignItems:'center', marginBottom:'8px' }}>
              <div style={{ fontSize:'12px', color:C.charcoal, fontWeight:700 }}>LM Total</div>
              <div style={{ padding:'8px 10px', background:C.lmLight, border:`1.5px solid ${C.lm}40`, borderRadius:'8px', textAlign:'right', fontSize:'13px', fontWeight:800, color:C.lm }}>
                {fmt(lmTotal)}
              </div>
              <div/>
            </div>
          </>
        )}

        {/* ── EBITDA ── */}
        <div style={{ background:actualEBITDA>=0?C.greenLight:C.redLight, border:`2px solid ${actualEBITDA>=0?C.green:C.red}30`, borderRadius:'10px', padding:'12px 16px', marginTop:'14px', marginBottom:'12px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 1fr', gap:'8px', alignItems:'center' }}>
            <div style={{ fontSize:'12px', fontWeight:800, color:C.charcoal }}>EBITDA (auto)</div>
            <div style={{ fontSize:'16px', fontWeight:900, color:actualEBITDA>=0?C.green:C.red, textAlign:'center' }}>
              {actualRev>0 ? fmt(Math.round(actualEBITDA)) : '\u2014'}
            </div>
            <div style={{ fontSize:'16px', fontWeight:900, color:budgetEBITDA>=0?C.green:C.red, textAlign:'center' }}>
              {budgetRev>0 ? fmt(Math.round(budgetEBITDA)) : '\u2014'}
            </div>
          </div>
        </div>

        {/* ── Depreciation & EBIT ── */}
        <div style={sectionBand(C.navy)}>Depreciation & EBIT</div>
        <CostRow label='Depreciation (€)' actualKey='depreciation' budgetKey='budget_depreciation'/>
        <div style={{ background:actualEBIT>=0?C.greenLight:C.redLight, border:`2px solid ${actualEBIT>=0?C.green:C.red}30`, borderRadius:'10px', padding:'12px 16px', marginBottom:'12px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 1fr', gap:'8px', alignItems:'center' }}>
            <div style={{ fontSize:'12px', fontWeight:800, color:C.charcoal }}>EBIT (auto)</div>
            <div style={{ fontSize:'16px', fontWeight:900, color:actualEBIT>=0?C.green:C.red, textAlign:'center' }}>
              {toNum(form.depreciation)>0 ? fmt(Math.round(actualEBIT)) : '\u2014'}
            </div>
            <div style={{ fontSize:'16px', fontWeight:900, color:budgetEBIT>=0?C.green:C.red, textAlign:'center' }}>
              {toNum(form.budget_depreciation)>0 ? fmt(Math.round(budgetEBIT)) : '\u2014'}
            </div>
          </div>
        </div>

        <div style={sectionBand(C.navy)}>Below the Line</div>
        <CostRow label='Net Profit (€)' actualKey='net_profit' budgetKey='budget_net_profit'/>

        <div style={{ display:'flex', gap:'10px', marginTop:'22px' }}>
          <button onClick={handleSave} disabled={saving} style={btnPrimary(saving, editingId ? C.amber : C.green)}>
            {saving ? 'Saving...' : editingId ? 'Update Month' : 'Save Month'}
          </button>
          {editingId && <button onClick={() => { setEditingId(null); setForm(emptyMonth(isSassys)); }} style={btnSecondary}>Cancel</button>}
        </div>
      </div>

      {/* Saved months list */}
      <div style={card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <h3 style={{ margin:0, fontSize:'15px', fontWeight:800, color:C.charcoal }}>Saved Months</h3>
          <div style={{ display:'flex', gap:'6px' }}>
            {YEARS.map(y => <button key={y} onClick={() => setYear(y)} style={pillStyle(year===y, C.navy)}>{y}</button>)}
          </div>
        </div>
        {records.length === 0
          ? <p style={{ color:C.mid, fontSize:'13px' }}>No data for {venue} {year}.</p>
          : records.map(r => {
              const ebitda = (r.actual_revenue||0)
                - (r.food_cost||0) - (r.bev_cost||0)
                - (r.oedipus_rent||0) - (r.oedipus_utilities||0) - (r.oedipus_rev_share||0)
                - (r.kitchen_payroll||0) - (r.foh_payroll||0)
                - (r.lm_prep_payroll_alloc||0) - (r.lm_transfers_alloc||0)
                - (r.group_costs||0) - (r.other_opex||0);
              const positive = ebitda >= 0;
              return (
                <div key={r.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:positive?C.greenLight:C.redLight, border:`1.5px solid ${positive?C.green:C.red}30`, borderRadius:'9px', marginBottom:'8px' }}>
                  <div>
                    <span style={{ fontSize:'13px', fontWeight:700, color:C.charcoal }}>{MONTHS[r.month-1]} {r.year}</span>
                    <div style={{ fontSize:'11px', color:C.mid, marginTop:'2px' }}>
                      Rev: <strong style={{ color:C.charcoal }}>{fmt(r.actual_revenue)}</strong>
                      {' · '}EBITDA: <strong style={{ color:positive?C.green:C.red }}>{fmt(Math.round(ebitda))}</strong>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:'6px' }}>
                    <button onClick={() => handleEdit(r)} style={{ ...btnSecondary, padding:'5px 12px', fontSize:'12px' }}>Edit</button>
                    <button onClick={async () => {
                      if (!window.confirm('Delete?')) return;
                      await supabase.from('results_monthly').delete().eq('id', r.id);
                      setRecords(prev => prev.filter(x => x.id !== r.id));
                      showToast('Deleted');
                    }} style={{ padding:'5px 10px', background:C.redLight, color:C.red, border:`1.5px solid ${C.red}30`, borderRadius:'7px', cursor:'pointer', fontSize:'12px', fontWeight:600 }}>Del</button>
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3 — Budget Overview (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
function BudgetTab({ venue }) {
  const [records, setRecords] = useState([]);
  const [year, setYear]       = useState(CURRENT_YEAR);
  const load = useCallback(async () => {
    const { data } = await supabase.from('results_monthly').select('*').eq('venue', venue).eq('year', year).order('month');
    if (data) setRecords(data);
  }, [venue, year]);
  useEffect(() => { load(); }, [load]);
  const setCount = records.filter(r => r.budget_revenue > 0).length;
  return (
    <div style={card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'18px' }}>
        <div>
          <h3 style={{ margin:0, fontSize:'15px', fontWeight:800, color:C.charcoal }}>Budget Overview — {venue}</h3>
          <p style={{ margin:'3px 0 0', fontSize:'12px', color:C.mid }}>
            <span style={{ background:setCount===12?C.greenLight:C.amberLight, color:setCount===12?C.green:C.amber, padding:'2px 8px', borderRadius:'5px', fontWeight:700 }}>{setCount}/12 months</span>
            {' '}set for {year} · Enter via Monthly P&L tab
          </p>
        </div>
        <div style={{ display:'flex', gap:'6px' }}>
          {YEARS.map(y => <button key={y} onClick={() => setYear(y)} style={pillStyle(year===y, C.navy)}>{y}</button>)}
        </div>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
          <thead>
            <tr style={{ background:C.charcoal }}>
              {['Month','Budget Rev','Budget Covers','Food %','Bev %','Payroll','Other OpEx','EBITDA'].map((h,i) => (
                <th key={h} style={{ padding:'10px 12px', textAlign:i===0?'left':'right', fontSize:'10px', fontWeight:700, color:i===0?C.yellow:'rgba(255,255,255,0.7)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MONTHS.map((m, i) => {
              const r = records.find(x => x.month === i+1);
              const pay = (r?.budget_kitchen_payroll||0) + (r?.budget_foh_payroll||0);
              const ebitda = (r?.budget_revenue||0) - (r?.budget_food_cost||0) - (r?.budget_bev_cost||0) - pay - (r?.budget_group_costs||0) - (r?.budget_other_opex||0);
              const hasBudget = r?.budget_revenue > 0;
              return (
                <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.white:C.cream }}>
                  <td style={{ padding:'10px 12px', fontWeight:700, color:C.charcoal }}>{m}</td>
                  <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, color:hasBudget?C.charcoal:C.border }}>{r?.budget_revenue?fmt(r.budget_revenue):'\u2014'}</td>
                  <td style={{ padding:'10px 12px', textAlign:'right' }}>{r?.budget_covers||'—'}</td>
                  <td style={{ padding:'10px 12px', textAlign:'right' }}>{r?.budget_revenue&&r?.budget_food_cost?((r.budget_food_cost/r.budget_revenue)*100).toFixed(1)+'%':'\u2014'}</td>
                  <td style={{ padding:'10px 12px', textAlign:'right' }}>{r?.budget_revenue&&r?.budget_bev_cost?((r.budget_bev_cost/r.budget_revenue)*100).toFixed(1)+'%':'\u2014'}</td>
                  <td style={{ padding:'10px 12px', textAlign:'right' }}>{pay?fmt(pay):'\u2014'}</td>
                  <td style={{ padding:'10px 12px', textAlign:'right' }}>{r?.budget_other_opex?fmt(r.budget_other_opex):'\u2014'}</td>
                  <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:800, color:hasBudget?(ebitda>0?C.green:C.red):C.border }}>{hasBudget?fmt(Math.round(ebitda)):'\u2014'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LEKKER MATE TAB — drop-in replacement for LekkerMateTab() in DataEntry.js
// Reads/writes to lekkermate_monthly table only.
// All allocation outputs computed client-side (Option B).
// FIXES:
//   1. pctValid() and SplitRow total now use toNum() — prevents string concatenation bug
//   2. Management fees included in LM Internal P&L cost side + net position
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_SPLIT = { jordaan: 40, plantage: 40, sassys: 20 };
const LM_VENUES = [
  { key: 'jordaan',  name: 'Box Jordaan',  label: 'Jordaan'  },
  { key: 'plantage', name: 'Box Plantage', label: 'Plantage' },
  { key: 'sassys',   name: 'Sassys',       label: 'Sassys'   },
];

// ── Core allocation engine ────────────────────────────────────────────────────
function computeAllocations({
  prep_payroll, mgmt_payroll, food_cost, other_costs,
  mgmt_fee_jordaan, mgmt_fee_plantage, mgmt_fee_sassys,
  direct_jordaan, direct_plantage, direct_sassys,
  food_pct_jordaan, food_pct_plantage, food_pct_sassys,
  payroll_pct_jordaan, payroll_pct_plantage, payroll_pct_sassys,
  other_pct_jordaan, other_pct_plantage, other_pct_sassys,
}) {
  const totalPayroll = (prep_payroll || 0) + (mgmt_payroll || 0);

  // Base allocations before direct payment adjustment
  const base = {
    jordaan:  {
      food:    Math.round((food_cost   || 0) * (food_pct_jordaan   || 0) / 100),
      payroll: Math.round(totalPayroll       * (payroll_pct_jordaan || 0) / 100),
      other:   Math.round((other_costs || 0) * (other_pct_jordaan  || 0) / 100),
      mgmt:    mgmt_fee_jordaan  || 0,
    },
    plantage: {
      food:    Math.round((food_cost   || 0) * (food_pct_plantage   || 0) / 100),
      payroll: Math.round(totalPayroll       * (payroll_pct_plantage || 0) / 100),
      other:   Math.round((other_costs || 0) * (other_pct_plantage  || 0) / 100),
      mgmt:    mgmt_fee_plantage || 0,
    },
    sassys:   {
      food:    Math.round((food_cost   || 0) * (food_pct_sassys   || 0) / 100),
      payroll: Math.round(totalPayroll       * (payroll_pct_sassys || 0) / 100),
      other:   Math.round((other_costs || 0) * (other_pct_sassys  || 0) / 100),
      mgmt:    mgmt_fee_sassys   || 0,
    },
  };

  // Direct payment adjustment — applies to Other first, then Food, never Payroll/Mgmt
  const directs = {
    jordaan:  direct_jordaan  || 0,
    plantage: direct_plantage || 0,
    sassys:   direct_sassys   || 0,
  };

  const otherPct = {
    jordaan:  other_pct_jordaan  || 0,
    plantage: other_pct_plantage || 0,
    sassys:   other_pct_sassys   || 0,
  };
  const foodPct = {
    jordaan:  food_pct_jordaan  || 0,
    plantage: food_pct_plantage || 0,
    sassys:   food_pct_sassys   || 0,
  };

  // Clone base for adjustment
  const adj = {
    jordaan:  { ...base.jordaan  },
    plantage: { ...base.plantage },
    sassys:   { ...base.sassys   },
  };

  // Process each venue's direct payment
  LM_VENUES.forEach(({ key: payer }) => {
    const D = directs[payer];
    if (!D || D <= 0) return;

    const payerPct = otherPct[payer];
    const payerReduction = Math.round(D * (100 - payerPct) / 100);

    let remaining = payerReduction;

    // Step 1: reduce Other
    const otherReduction = Math.min(adj[payer].other, remaining);
    adj[payer].other -= otherReduction;
    remaining -= otherReduction;

    // Step 2: reduce Food if Other exhausted
    if (remaining > 0) {
      const foodReduction = Math.min(adj[payer].food, remaining);
      adj[payer].food -= foodReduction;
      remaining -= foodReduction;
    }

    // Other venues absorb their share of D
    LM_VENUES.forEach(({ key: other }) => {
      if (other === payer) return;
      const increase = Math.round(D * otherPct[other] / 100);
      adj[other].other += increase;
    });
  });

  // Totals
  LM_VENUES.forEach(({ key }) => {
    adj[key].total = adj[key].food + adj[key].payroll + adj[key].other + adj[key].mgmt;
  });

  return adj;
}

// ── LekkerMateTab component ───────────────────────────────────────────────────
function LekkerMateTab() {
  const [month,  setMonth]  = useState(currentMonth);
  const [year,   setYear]   = useState(CURRENT_YEAR);
  const [record, setRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast,  setToast]  = useState(null);

  // Costs In
  const [prepPayroll,  setPrepPayroll]  = useState('');
  const [mgmtPayroll,  setMgmtPayroll]  = useState('');
  const [foodCost,     setFoodCost]     = useState('');
  const [otherCosts,   setOtherCosts]   = useState('');

  // Management fees per venue
  const [mgmtFees, setMgmtFees] = useState({ jordaan: '', plantage: '', sassys: '' });

  // Direct venue payments
  const [directs, setDirects] = useState({ jordaan: '', plantage: '', sassys: '' });

  // Split %
  const [foodPct,    setFoodPct]    = useState({ ...DEFAULT_SPLIT });
  const [payrollPct, setPayrollPct] = useState({ ...DEFAULT_SPLIT });
  const [otherPct,   setOtherPct]   = useState({ ...DEFAULT_SPLIT });

  // Prior month food revenues for auto food split
  const [priorFoodRevs, setPriorFoodRevs] = useState({ jordaan: 0, plantage: 0, sassys: 0 });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Get prior month
  const getPrior = () => {
    let pm = month - 1, py = year;
    if (pm === 0) { pm = 12; py = year - 1; }
    return { pm, py, label: `${MONTHS[pm - 1]} ${py}` };
  };

  const load = useCallback(async () => {
    // Load this month's LM record
    const { data } = await supabase
      .from('lekkermate_monthly')
      .select('*')
      .eq('year', year)
      .eq('month', month)
      .single();

    // Load prior month food revenues for auto-split
    const { pm, py } = getPrior();
    const priorResults = await Promise.all(
      LM_VENUES.map(({ name }) =>
        supabase.from('results_monthly')
          .select('food_revenue')
          .eq('venue', name).eq('year', py).eq('month', pm)
          .single()
      )
    );
    const priorMap = {};
    LM_VENUES.forEach(({ key }, i) => {
      priorMap[key] = priorResults[i].data?.food_revenue || 0;
    });
    setPriorFoodRevs(priorMap);

    if (data) {
      setRecord(data);
      setPrepPayroll(data.prep_payroll  || '');
      setMgmtPayroll(data.mgmt_payroll  || '');
      setFoodCost(data.food_cost        || '');
      setOtherCosts(data.other_costs    || '');
      setMgmtFees({
        jordaan:  data.mgmt_fee_jordaan  || '',
        plantage: data.mgmt_fee_plantage || '',
        sassys:   data.mgmt_fee_sassys   || '',
      });
      setDirects({
        jordaan:  data.direct_jordaan  || '',
        plantage: data.direct_plantage || '',
        sassys:   data.direct_sassys   || '',
      });
      setFoodPct({
        jordaan:  data.food_pct_jordaan  || 0,
        plantage: data.food_pct_plantage || 0,
        sassys:   data.food_pct_sassys   || 0,
      });
      setPayrollPct({
        jordaan:  data.payroll_pct_jordaan  || DEFAULT_SPLIT.jordaan,
        plantage: data.payroll_pct_plantage || DEFAULT_SPLIT.plantage,
        sassys:   data.payroll_pct_sassys   || DEFAULT_SPLIT.sassys,
      });
      setOtherPct({
        jordaan:  data.other_pct_jordaan  || DEFAULT_SPLIT.jordaan,
        plantage: data.other_pct_plantage || DEFAULT_SPLIT.plantage,
        sassys:   data.other_pct_sassys   || DEFAULT_SPLIT.sassys,
      });
    } else {
      setRecord(null);
      setPrepPayroll(''); setMgmtPayroll('');
      setFoodCost(''); setOtherCosts('');
      setMgmtFees({ jordaan: '', plantage: '', sassys: '' });
      setDirects({ jordaan: '', plantage: '', sassys: '' });
      setPayrollPct({ ...DEFAULT_SPLIT });
      setOtherPct({ ...DEFAULT_SPLIT });

      // Auto-set food pct from prior food revenues
      const totalPrior = Object.values(priorMap).reduce((s, v) => s + v, 0);
      if (totalPrior > 0) {
        setFoodPct({
          jordaan:  Math.round((priorMap.jordaan  / totalPrior) * 100),
          plantage: Math.round((priorMap.plantage / totalPrior) * 100),
          sassys:   Math.round((priorMap.sassys   / totalPrior) * 100),
        });
      } else {
        setFoodPct({ ...DEFAULT_SPLIT });
      }
    }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  // Auto-apply prior food rev % when prior data loads and no record exists
  useEffect(() => {
    if (record) return;
    const totalPrior = Object.values(priorFoodRevs).reduce((s, v) => s + v, 0);
    if (totalPrior > 0) {
      setFoodPct({
        jordaan:  Math.round((priorFoodRevs.jordaan  / totalPrior) * 100),
        plantage: Math.round((priorFoodRevs.plantage / totalPrior) * 100),
        sassys:   Math.round((priorFoodRevs.sassys   / totalPrior) * 100),
      });
    }
  }, [priorFoodRevs, record]);

  // FIX 1: wrap in toNum() to prevent string concatenation
  const pctValid = (p) => Math.round(toNum(p.jordaan) + toNum(p.plantage) + toNum(p.sassys)) === 100;

  // Live allocation compute
  const allocs = computeAllocations({
    prep_payroll:      toNum(prepPayroll),
    mgmt_payroll:      toNum(mgmtPayroll),
    food_cost:         toNum(foodCost),
    other_costs:       toNum(otherCosts),
    mgmt_fee_jordaan:  toNum(mgmtFees.jordaan),
    mgmt_fee_plantage: toNum(mgmtFees.plantage),
    mgmt_fee_sassys:   toNum(mgmtFees.sassys),
    direct_jordaan:    toNum(directs.jordaan),
    direct_plantage:   toNum(directs.plantage),
    direct_sassys:     toNum(directs.sassys),
    food_pct_jordaan:  toNum(foodPct.jordaan),
    food_pct_plantage: toNum(foodPct.plantage),
    food_pct_sassys:   toNum(foodPct.sassys),
    payroll_pct_jordaan:  toNum(payrollPct.jordaan),
    payroll_pct_plantage: toNum(payrollPct.plantage),
    payroll_pct_sassys:   toNum(payrollPct.sassys),
    other_pct_jordaan:  toNum(otherPct.jordaan),
    other_pct_plantage: toNum(otherPct.plantage),
    other_pct_sassys:   toNum(otherPct.sassys),
  });

  const totalCosts = toNum(prepPayroll) + toNum(mgmtPayroll) + toNum(foodCost) + toNum(otherCosts)
    + toNum(mgmtFees.jordaan) + toNum(mgmtFees.plantage) + toNum(mgmtFees.sassys);

  // FIX 2: total mgmt fees for P&L cost side
  const totalMgmt = toNum(mgmtFees.jordaan) + toNum(mgmtFees.plantage) + toNum(mgmtFees.sassys);

  const handleSave = async () => {
    if (!pctValid(foodPct))    { showToast('Food % must sum to 100%', 'error'); return; }
    if (!pctValid(payrollPct)) { showToast('Payroll % must sum to 100%', 'error'); return; }
    if (!pctValid(otherPct))   { showToast('Other % must sum to 100%', 'error'); return; }

    setSaving(true);

    // Allocation outputs — saved alongside inputs so Results page can read directly
    const payload = {
      year, month,
      prep_payroll:      toNum(prepPayroll),
      mgmt_payroll:      toNum(mgmtPayroll),
      food_cost:         toNum(foodCost),
      other_costs:       toNum(otherCosts),
      mgmt_fee_jordaan:  toNum(mgmtFees.jordaan),
      mgmt_fee_plantage: toNum(mgmtFees.plantage),
      mgmt_fee_sassys:   toNum(mgmtFees.sassys),
      direct_jordaan:    toNum(directs.jordaan),
      direct_plantage:   toNum(directs.plantage),
      direct_sassys:     toNum(directs.sassys),
      food_pct_jordaan:  toNum(foodPct.jordaan),
      food_pct_plantage: toNum(foodPct.plantage),
      food_pct_sassys:   toNum(foodPct.sassys),
      payroll_pct_jordaan:  toNum(payrollPct.jordaan),
      payroll_pct_plantage: toNum(payrollPct.plantage),
      payroll_pct_sassys:   toNum(payrollPct.sassys),
      other_pct_jordaan:  toNum(otherPct.jordaan),
      other_pct_plantage: toNum(otherPct.plantage),
      other_pct_sassys:   toNum(otherPct.sassys),
      // Computed allocation outputs
      alloc_jordaan_food:    allocs.jordaan.food,
      alloc_jordaan_payroll: allocs.jordaan.payroll,
      alloc_jordaan_other:   allocs.jordaan.other,
      alloc_jordaan_mgmt:    allocs.jordaan.mgmt,
      alloc_jordaan_total:   allocs.jordaan.total,
      alloc_plantage_food:    allocs.plantage.food,
      alloc_plantage_payroll: allocs.plantage.payroll,
      alloc_plantage_other:   allocs.plantage.other,
      alloc_plantage_mgmt:    allocs.plantage.mgmt,
      alloc_plantage_total:   allocs.plantage.total,
      alloc_sassys_food:    allocs.sassys.food,
      alloc_sassys_payroll: allocs.sassys.payroll,
      alloc_sassys_other:   allocs.sassys.other,
      alloc_sassys_mgmt:    allocs.sassys.mgmt,
      alloc_sassys_total:   allocs.sassys.total,
      updated_at: new Date().toISOString(),
    };

    if (record) {
      const { error } = await supabase.from('lekkermate_monthly').update(payload).eq('id', record.id);
      if (error) { showToast('Error: ' + error.message, 'error'); setSaving(false); return; }
    } else {
      const { error } = await supabase.from('lekkermate_monthly').insert([payload]);
      if (error) { showToast('Error: ' + error.message, 'error'); setSaving(false); return; }
    }

    showToast(`${MONTHS[month - 1]} ${year} saved`);
    setSaving(false);
    load();
  };

  // ── UI helpers ──────────────────────────────────────────────────────────────
  const { label: priorLabel } = getPrior();

  const SplitRow = ({ label, pct, setPct, autoLabel }) => {
    const valid = pctValid(pct);
    // FIX 1: toNum() here too — prevents string concatenation in display
    const total = Math.round(toNum(pct.jordaan) + toNum(pct.plantage) + toNum(pct.sassys));
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.charcoal }}>{label}</span>
          {autoLabel && <span style={{ fontSize: 10, color: C.mid, background: C.cream, padding: '1px 6px', borderRadius: 4 }}>{autoLabel}</span>}
          {!valid && <span style={{ fontSize: 10, color: C.red, fontWeight: 700 }}>Must sum to 100% (currently {total}%)</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {LM_VENUES.map(({ key, label: vLabel }) => (
            <div key={key} style={{ textAlign: 'center', background: C.cream, borderRadius: 8, padding: '8px 6px', border: `1px solid ${valid ? C.border : C.red + '40'}` }}>
              <div style={{ fontSize: 10, color: C.mid, marginBottom: 4, fontWeight: 600 }}>{vLabel}</div>
              <input
                type="number" min={0} max={100}
                value={pct[key]}
                onChange={e => setPct(prev => ({ ...prev, [key]: e.target.value }))}
                style={{ ...inputStyle, width: 60, textAlign: 'center', padding: '5px 4px' }}
              />
              <div style={{ fontSize: 10, color: C.lm, marginTop: 3, fontWeight: 700 }}>
                {pct[key] || 0}%
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const CostInRow = ({ label, value, onChange, color }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'center', marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.charcoal }}>{label}</div>
      <DebounceInput value={value} onChange={onChange} placeholder="0"
        style={{ background: color ? `${color}10` : C.white, borderColor: color ? `${color}40` : C.border }}
      />
    </div>
  );

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '14px 20px', background: C.lmLight, border: `2px solid ${C.lm}40`, borderRadius: 12 }}>
        <div style={{ width: 6, height: 40, background: C.lm, borderRadius: 4, flexShrink: 0 }}/>
        <div>
          <div style={{ fontWeight: 900, fontSize: 16, color: C.lm }}>Lekker Mate Cost Centre</div>
          <div style={{ fontSize: 12, color: C.mid, marginTop: 2 }}>Track group costs · Allocations computed per venue · Internal only</div>
        </div>
        {record && (
          <span style={{ marginLeft: 'auto', fontSize: 11, background: C.greenLight, color: C.green, fontWeight: 700, padding: '5px 12px', borderRadius: 20, border: `1px solid ${C.green}40` }}>
            ✓ Saved — {MONTHS[month - 1]} {year}
          </span>
        )}
      </div>

      {/* Month / year selectors */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {MONTHS.map((m, i) => (
          <button key={i} onClick={() => setMonth(i + 1)}
            style={{ ...pillStyle(month === i + 1, C.lm), padding: '5px 12px', fontSize: 12 }}>
            {m}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {YEARS.map(y => (
          <button key={y} onClick={() => setYear(y)} style={pillStyle(year === y, C.charcoal)}>{y}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 24 }}>

        {/* ── LEFT: Costs In ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={card}>
            <div style={{ fontWeight: 800, fontSize: 14, color: C.charcoal, marginBottom: 4 }}>Costs In — {MONTHS[month - 1]} {year}</div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 16 }}>Total LM costs for the month</div>

            <div style={sectionBand(C.purple)}>Payroll</div>
            <CostInRow label="Prep Kitchen (€)"   value={prepPayroll} onChange={setPrepPayroll} color={C.purple}/>
            <CostInRow label="Group Mgmt (€)"      value={mgmtPayroll} onChange={setMgmtPayroll} color={C.purple}/>

            <div style={sectionBand(C.orange)}>Food Cost</div>
            <CostInRow label="Total Food (€)" value={foodCost} onChange={setFoodCost} color={C.orange}/>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 8, padding: '6px 10px', background: C.cream, borderRadius: 7 }}>
              Split by prior month food revenue % ({priorLabel})
              {Object.values(priorFoodRevs).some(v => v > 0) && (
                <span style={{ marginLeft: 6, color: C.charcoal, fontWeight: 600 }}>
                  J: {fmt(priorFoodRevs.jordaan)} · P: {fmt(priorFoodRevs.plantage)} · S: {fmt(priorFoodRevs.sassys)}
                </span>
              )}
            </div>

            <div style={sectionBand(C.navy)}>Management Fee</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
              {LM_VENUES.map(({ key, label }) => (
                <div key={key} style={{ textAlign: 'center', background: C.cream, borderRadius: 8, padding: '10px 6px' }}>
                  <div style={{ fontSize: 10, color: C.mid, marginBottom: 5, fontWeight: 600 }}>{label}</div>
                  <DebounceInput
                    value={mgmtFees[key]}
                    onChange={v => setMgmtFees(prev => ({ ...prev, [key]: v }))}
                    placeholder="0"
                    style={{ textAlign: 'center', padding: '6px 4px' }}
                  />
                </div>
              ))}
            </div>

            <div style={sectionBand(C.green)}>Other Costs</div>
            <CostInRow label="Total Other (€)" value={otherCosts} onChange={setOtherCosts} color={C.green}/>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 8 }}>Insurance, software, sundry shared costs etc.</div>

            {/* Total */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '12px 16px', background: C.cream, borderRadius: 9, border: `1.5px solid ${C.border}` }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}>Total LM Costs</span>
              <span style={{ fontSize: 18, fontWeight: 900, color: totalCosts > 0 ? C.red : C.border }}>{totalCosts > 0 ? fmt(totalCosts) : '—'}</span>
            </div>
          </div>

          {/* Direct venue payments */}
          <div style={card}>
            <div style={{ fontWeight: 800, fontSize: 14, color: C.charcoal, marginBottom: 4 }}>Direct Venue Payments</div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 14, lineHeight: 1.6 }}>
              If a venue paid a group-related cost directly, enter it here. Their net charge reduces by the other venues' combined %, and the other venues absorb their proportional share.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {LM_VENUES.map(({ key, label }) => (
                <div key={key} style={{ textAlign: 'center', background: C.cream, borderRadius: 8, padding: '10px 6px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, marginBottom: 6 }}>{label}</div>
                  <DebounceInput
                    value={directs[key]}
                    onChange={v => setDirects(prev => ({ ...prev, [key]: v }))}
                    placeholder="0"
                    style={{ textAlign: 'center' }}
                  />
                  {toNum(directs[key]) > 0 && (
                    <div style={{ fontSize: 10, color: C.green, marginTop: 3, fontWeight: 700 }}>
                      Net reduction: {fmt(Math.round(toNum(directs[key]) * (100 - toNum(otherPct[key])) / 100))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Split config */}
          <div style={card}>
            <div style={{ fontWeight: 800, fontSize: 14, color: C.charcoal, marginBottom: 4 }}>Allocation Config</div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 16 }}>How costs are split across venues. Saved per month.</div>

            <SplitRow
              label="Food Cost %"
              pct={foodPct}
              setPct={setFoodPct}
              autoLabel={`auto from ${priorLabel} food rev`}
            />
            <SplitRow
              label="Payroll %"
              pct={payrollPct}
              setPct={setPayrollPct}
            />
            <SplitRow
              label="Other Costs %"
              pct={otherPct}
              setPct={setOtherPct}
            />
          </div>
        </div>

        {/* ── RIGHT: Allocation output ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={card}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.charcoal, marginBottom: 4 }}>
              Allocations — {MONTHS[month - 1]} {year}
            </div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 16 }}>Live preview · updates as you type</div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: C.charcoal }}>
                  {['Cost Line', 'Jordaan', 'Plantage', 'Sassys'].map((h, i) => (
                    <th key={h} style={{ padding: '9px 10px', textAlign: i === 0 ? 'left' : 'right', fontSize: 10, fontWeight: 700, color: i === 0 ? C.yellow : 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Food Cost',      key: 'food',    color: C.orange },
                  { label: 'Payroll',        key: 'payroll', color: C.purple },
                  { label: 'Other Costs',    key: 'other',   color: C.green  },
                  { label: 'Management Fee', key: 'mgmt',    color: C.navy   },
                ].map(({ label, key, color }, i) => (
                  <tr key={key} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.cream }}>
                    <td style={{ padding: '9px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: 'inline-block', flexShrink: 0 }}/>
                      <span style={{ fontWeight: 600, color: C.charcoal }}>{label}</span>
                    </td>
                    {LM_VENUES.map(({ key: vk }) => (
                      <td key={vk} style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600, color: allocs[vk][key] > 0 ? C.charcoal : C.border }}>
                        {allocs[vk][key] > 0 ? fmt(allocs[vk][key]) : '—'}
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Direct payment note rows */}
                {LM_VENUES.some(({ key }) => toNum(directs[key]) > 0) && (
                  <tr style={{ background: C.skyBlueLight, borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '7px 10px', fontSize: 10, color: C.skyText, fontStyle: 'italic' }}>↳ Direct payment adj.</td>
                    {LM_VENUES.map(({ key }) => {
                      const d = toNum(directs[key]);
                      const reduction = d > 0 ? Math.round(d * (100 - toNum(otherPct[key])) / 100) : 0;
                      return (
                        <td key={key} style={{ padding: '7px 10px', textAlign: 'right', fontSize: 10, color: d > 0 ? C.green : C.skyText, fontStyle: 'italic' }}>
                          {d > 0 ? `−${fmt(reduction)}` : '—'}
                        </td>
                      );
                    })}
                  </tr>
                )}

                {/* Total row */}
                <tr style={{ background: C.lmLight, borderTop: `2px solid ${C.lm}40` }}>
                  <td style={{ padding: '11px 10px', fontWeight: 800, color: C.lm }}>Total to Charge</td>
                  {LM_VENUES.map(({ key }) => (
                    <td key={key} style={{ padding: '11px 10px', textAlign: 'right', fontWeight: 900, fontSize: 14, color: allocs[key].total > 0 ? C.lm : C.border }}>
                      {allocs[key].total > 0 ? fmt(allocs[key].total) : '—'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>

            <div style={{ marginTop: 14, padding: '10px 14px', background: C.cream, borderRadius: 8, fontSize: 11, color: C.mid, lineHeight: 1.6 }}>
              ℹ️ Jordaan + Plantage = intercompany invoices. Sassys = memo allocation (same legal entity, no invoice).
            </div>
          </div>

          {/* LM P&L summary */}
          <div style={card}>
            <div style={{ fontWeight: 800, fontSize: 14, color: C.charcoal, marginBottom: 14 }}>LM Internal P&L — {MONTHS[month - 1]} {year}</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {[
                  { label: 'Recharged → Jordaan',  value: allocs.jordaan.total,  isRev: true  },
                  { label: 'Recharged → Plantage', value: allocs.plantage.total, isRev: true  },
                  { label: 'Memo → Sassys',        value: allocs.sassys.total,   isRev: false, isMemo: true },
                  { label: '─────', value: null },
                  { label: 'Prep Kitchen Payroll', value: toNum(prepPayroll) },
                  { label: 'Group Mgmt Payroll',   value: toNum(mgmtPayroll) },
                  { label: 'Food Cost',             value: toNum(foodCost)    },
                  { label: 'Other Costs',           value: toNum(otherCosts)  },
                  // FIX 2: management fees now included on cost side
                  { label: 'Management Fees',       value: totalMgmt          },
                ].map((row, i) => {
                  if (!row.label.startsWith('─') && row.value === null) return null;
                  if (row.label.startsWith('─')) return (
                    <tr key={i}><td colSpan={2} style={{ padding: '4px 0', borderBottom: `1px solid ${C.border}` }}/></tr>
                  );
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '7px 10px', fontSize: 12, color: row.isMemo ? C.mid : C.charcoal, fontStyle: row.isMemo ? 'italic' : 'normal' }}>{row.label}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: row.isRev ? C.green : row.isMemo ? C.mid : row.value > 0 ? C.charcoal : C.border }}>
                        {row.value > 0 ? (row.isRev ? '+' : '−') + fmt(row.value) : '—'}
                      </td>
                    </tr>
                  );
                })}
                {(() => {
                  const income = allocs.jordaan.total + allocs.plantage.total;
                  // FIX 2: totalMgmt included in costs so net zeros out correctly
                  const costs  = toNum(prepPayroll) + toNum(mgmtPayroll) + toNum(foodCost) + toNum(otherCosts) + totalMgmt;
                  const net    = income - costs;
                  const positive = net >= 0;
                  return (
                    <tr style={{ background: positive ? C.greenLight : C.redLight }}>
                      <td style={{ padding: 10, fontWeight: 800, color: C.charcoal }}>Net Position</td>
                      <td style={{ padding: 10, textAlign: 'right', fontWeight: 900, fontSize: 15, color: positive ? C.green : C.red }}>
                        {costs > 0 ? fmt(Math.round(net)) : '—'}
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>

          {/* Save button */}
          <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary(saving, C.lm), width: '100%', padding: 14, fontSize: 14 }}>
            {saving ? 'Saving…' : `Save — ${MONTHS[month - 1]} ${year}`}
          </button>
        </div>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────
export default function DataEntry({ currentUser }) {
  const isAdmin   = isAdminUser(currentUser);
  const [tab, setTab]     = useState('weekly');
  const [venue, setVenue] = useState(VENUES[0]);

  if (!isAdmin) return (
    <div style={{ padding:'3rem', textAlign:'center', color:C.mid }}>
      <div style={{ fontSize:'32px', marginBottom:'12px' }}>🔒</div>
      <div style={{ fontSize:'16px', fontWeight:700, color:C.charcoal }}>Access restricted to admins only.</div>
    </div>
  );

  const tabs = [
    { key:'weekly',  label:'Weekly'         },
    { key:'monthly', label:'Monthly P&L'    },
    { key:'budget',  label:'Budget'         },
    { key:'lekker',  label:'🍋 Lekker Mate' },
  ];

  // These tabs don't use the venue selector
  const venueTabsDisabled = ['lekker'].includes(tab);

  const tabAccent = {
    weekly:  C.navy,
    monthly: C.green,
    budget:  C.amber,
    lekker:  C.lm,
  };

  return (
    <div style={{ padding:'24px', maxWidth:'1400px', fontFamily:'inherit' }}>
      <div style={{ marginBottom:'24px', paddingBottom:'20px', borderBottom:`2px solid ${C.border}` }}>
        <h2 style={{ margin:0, fontSize:'24px', fontWeight:900, color:C.charcoal, letterSpacing:'-0.02em' }}>Data Entry</h2>
        <p style={{ margin:'4px 0 0', fontSize:'13px', color:C.mid }}>Admin only — feeds Results and Governance pages</p>
      </div>

      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'28px', alignItems:'center', padding:'12px 16px', background:C.cream, borderRadius:'12px', border:`1.5px solid ${C.border}` }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={pillStyle(tab===t.key, tabAccent[t.key])}>{t.label}</button>
        ))}
        <div style={{ width:'1px', background:C.border, height:'20px', margin:'0 6px' }}/>
        {VENUES.map(v => (
          <button key={v} onClick={() => { if (!venueTabsDisabled) setVenue(v); }}
            style={{ ...pillStyle(!venueTabsDisabled && venue===v, C.charcoal), opacity:venueTabsDisabled?0.35:1, cursor:venueTabsDisabled?'default':'pointer' }}>
            {v}
          </button>
        ))}
      </div>

      {tab==='weekly'  && <WeeklyTab  venue={venue}/>}
      {tab==='monthly' && <MonthlyTab venue={venue}/>}
      {tab==='budget'  && <BudgetTab  venue={venue}/>}
      {tab==='lekker'  && <LekkerMateTab/>}
    </div>
  );
}